import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { SideMenuItem } from '@/app/ui/SideMenu/config';

import SettingsHoverPopover from './SettingsHoverPopover';
import SideMenuSubitem from './SideMenuSubitem';
import StatisticsHoverPopover from './StatisticsHoverPopover';
import {
  ActiveIndicator,
  ButtonIconWrapper,
  ButtonLabel,
  ButtonText,
  MenuButton,
  SubMenu,
} from './styled';
import useSideMenuHoverPopover from './useSideMenuHoverPopover';

interface Props {
  menuItem: SideMenuItem;
  isActiveRoute: (menuItem: SideMenuItem) => boolean;
  isExpanded: boolean;
  showSubmenu: boolean;
  setShowSubmenu: Dispatch<SetStateAction<boolean>>;
  onHoverStart: (closeSelf: () => void) => void;
}

const SideMenuListItem: FC<Props> = ({
  menuItem,
  isActiveRoute,
  isExpanded,
  showSubmenu,
  setShowSubmenu,
  onHoverStart,
}) => {
  const { label, icon, path, disabled, submenus, tooltip } = menuItem;

  const navigate = useNavigate();
  const location = useLocation();
  const isActive = isActiveRoute(menuItem);

  const isStatisticsItem = !!menuItem.isStatisticsMenu && !!submenus?.length;
  const isHoverPopoverItem = !isStatisticsItem && !!submenus?.length;
  const { handleMouseEnter, handleMouseLeave, hoverPopover, closePopover } =
    useSideMenuHoverPopover(tooltip ?? label, isExpanded || isStatisticsItem || isHoverPopoverItem);

  const [statsAnchorEl, setStatsAnchorEl] = useState<HTMLElement | null>(null);
  const statsCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setStatsAnchorEl(null);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (hoverOpenTimerRef.current) clearTimeout(hoverOpenTimerRef.current);
      if (statsCloseTimerRef.current) clearTimeout(statsCloseTimerRef.current);
    };
  }, []);

  const closeThisHover = () => {
    if (hoverOpenTimerRef.current) clearTimeout(hoverOpenTimerRef.current);
    if (statsCloseTimerRef.current) clearTimeout(statsCloseTimerRef.current);
    setStatsAnchorEl(null);
    closePopover();
  };

  const handleStatsMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (isExpanded) return;
    onHoverStart(closeThisHover);
    if (statsCloseTimerRef.current) clearTimeout(statsCloseTimerRef.current);
    if (isHoverPopoverItem) {
      const target = event.currentTarget;
      hoverOpenTimerRef.current = setTimeout(() => setStatsAnchorEl(target), 300);
    } else {
      setStatsAnchorEl(event.currentTarget);
    }
  };

  const handleStatsMouseLeave = () => {
    if (hoverOpenTimerRef.current) clearTimeout(hoverOpenTimerRef.current);
    statsCloseTimerRef.current = setTimeout(() => setStatsAnchorEl(null), 200);
  };

  const handleLabelMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    onHoverStart(closeThisHover);
    handleMouseEnter(event);
  };

  return (
    <>
      <MenuButton
        variant="text"
        disabled={disabled}
        isActive={isActive && (!submenus?.length || !isExpanded)}
        isExpanded={isExpanded}
        data-testid={`menu-${label.toLowerCase().replace(/\s+/g, '-')}`}
        onMouseEnter={
          isStatisticsItem || isHoverPopoverItem ? handleStatsMouseEnter : handleLabelMouseEnter
        }
        onMouseLeave={
          isStatisticsItem || isHoverPopoverItem ? handleStatsMouseLeave : handleMouseLeave
        }
        onClick={() => {
          handleMouseLeave();
          handleStatsMouseLeave();
          if (path) {
            setShowSubmenu(false);
            navigate(path);
          } else {
            setShowSubmenu(true);
          }
        }}
      >
        {isActive && (!submenus?.length || !isExpanded) && <ActiveIndicator />}

        <ButtonLabel isActive={isActive && (!submenus?.length || !isExpanded)}>
          <ButtonIconWrapper isExpanded={isExpanded}>
            {icon}

            <ButtonText
              isActive={isActive && (!submenus?.length || !isExpanded)}
              isExpanded={isExpanded}
            >
              {label}
            </ButtonText>
          </ButtonIconWrapper>

          {!!submenus && isExpanded && (
            <span style={{ display: 'flex', padding: 0, pointerEvents: 'none' }}>
              {isActive ? (
                <ChevronDownIcon sx={{ fontSize: '1.25rem' }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
              )}
            </span>
          )}
        </ButtonLabel>
      </MenuButton>

      {hoverPopover}

      {isStatisticsItem && (
        <StatisticsHoverPopover
          anchorEl={statsAnchorEl}
          submenus={submenus}
          onClose={() => setStatsAnchorEl(null)}
          onMouseEnter={() => {
            if (statsCloseTimerRef.current) clearTimeout(statsCloseTimerRef.current);
          }}
          onMouseLeave={handleStatsMouseLeave}
        />
      )}

      {isHoverPopoverItem && (
        <SettingsHoverPopover
          anchorEl={statsAnchorEl}
          title={label}
          submenus={submenus}
          isActiveRoute={isActiveRoute}
          onClose={() => setStatsAnchorEl(null)}
          onMouseEnter={() => {
            if (statsCloseTimerRef.current) clearTimeout(statsCloseTimerRef.current);
          }}
          onMouseLeave={handleStatsMouseLeave}
        />
      )}

      {!!submenus && isExpanded && (isActive || (showSubmenu && !path)) && (
        <SubMenu isExpanded={isExpanded}>
          {submenus.map((submenu) => (
            <SideMenuSubitem
              key={submenu.label}
              submenuItem={submenu}
              isActiveRoute={isActiveRoute}
              isExpanded={isExpanded}
              isStatistics={!!menuItem.isStatisticsMenu}
              collapsed={submenus.length === 1}
            />
          ))}
        </SubMenu>
      )}
    </>
  );
};

export default SideMenuListItem;
